<script setup lang="ts">
import { computed, ref, watchEffect } from 'vue'

type AsidePosition = 'left' | 'right'
type AsideSize = 'md'

interface Props {
  position?: AsidePosition
  size?: AsideSize
  closeOnOverlayClick?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  position: 'right',
  size: 'md',
  closeOnOverlayClick: true,
})

const emit = defineEmits<{
  (e: 'open'): void
  (e: 'close'): void
}>()

const model = defineModel<boolean>({ default: false })
const isOpen = ref(model.value)

function close() {
  model.value = false
  emit('close')
}

function open() {
  model.value = true
  emit('open')
}

const dialogEl = ref<HTMLDialogElement | null>(null)

function onDialogClick(e: MouseEvent) {
  if (e.target === e.currentTarget && props.closeOnOverlayClick) close()
}

// Keep native dialog in sync with v-model
watchEffect(() => {
  const el = dialogEl.value
  if (!el) return
  if (model.value && !el.open) {
    try { el.show() } catch {}
    isOpen.value = true
  }
  if (!model.value && el.open) {
    // let the leave transition run; closing happens after-leave
    isOpen.value = false
  }
})

function afterLeave() {
  const el = dialogEl.value
  if (el?.open) el.close()
}

const isRight = computed(() => props.position === 'right')

const panelBase = 'bg-white h-full w-full md:w-[550px] shadow-xl md:shadow-2xl'
const panelRadius = computed(() => (
  isRight.value ? 'md:rounded-l-lg' : 'md:rounded-r-lg'
))

const panelEnterFrom = computed(() => (isRight.value ? 'translate-x-full' : '-translate-x-full'))
const panelLeaveTo = panelEnterFrom

defineExpose({ open, close })
</script>

<template>
  <Teleport to="body" defer>
    <dialog
      ref="dialogEl"
      class="p-0 bg-transparent w-full max-w-none h-full fixed inset-0 z-[60] se-dialog-overlay max-h-none overflow-hidden"
      :class="[ isOpen ? 'se-scroll-lock' : '']"
      @close="model = false"
      @cancel.prevent="close"
      @click="onDialogClick"
    >
      <div
        aria-hidden="true"
        class="absolute inset-0 bg-black/40 pointer-events-auto"
        @click="onDialogClick"
      ></div>
      <!-- Panel wrapper: manages side and mobile full screen behavior -->
      <div class="absolute inset-0 pointer-events-none flex items-stretch">
        <div
          class="relative inset-y-0 w-full md:w-auto pointer-events-auto"
          :class="[ isRight ? 'ml-auto' : 'mr-auto' ]"
        >
          <Transition
            :enter-from-class="panelEnterFrom"
            enter-active-class="transition-transform duration-300 ease-out se-scroll-lock"
            :leave-to-class="panelLeaveTo"
            leave-active-class="transition-transform duration-300 ease-in"
            @after-leave="afterLeave"
          >
            <div v-show="isOpen" class="h-full flex">
              <div :class="[
                  panelBase,
                  panelRadius,
                  'max-md:rounded-none max-md:h-[100dvh] max-md:overflow-y-auto flex flex-col'
                ]">
                <slot />
              </div>
            </div>
          </Transition>
        </div>
      </div>
    </dialog>
  </Teleport>
</template>



